import _ from "underscore";
import { defineStore } from "pinia";

import { markService } from "@/services";
import { isApiError } from "@/services/Api";
import { getMarkStatus, isRectangleAnnotation } from "@/services/UtilitiesService";

import { ModuleStatus } from "@/models/ModuleStatus";

import { useCaseStore } from "@/stores/case-store";
import { useBrandStore } from "@/stores/brand-store";
import { useMessageStore } from "@/stores/message-store";
import { useEvidenceStore } from "@/stores/evidence-store";
import { useMetadataStore } from "@/stores/metadata-store";

import type { Mark, ParsedMark } from "@/models/Mark";
import type { HighlightCustomArea } from "@/models/Viewer";
import type { RectangleAnnotation, HighlightAnnotation, TranscriptAnnotation } from "@/models/Annotation";

export const useAnnotationStore = defineStore("annotation", {
  scope: "case",
  state: (): State => ({
    unfilteredAnnotations: [],
    moduleStatus: ModuleStatus.Initial,
  }),
  getters: {
    categories(): string[] {
      return [
        ...new Set(
          this.annotations.flatMap((a) => (a.evidenceThemes ?? []).map((c) => c.evidenceTheme).filter((c) => !!c)),
        ),
      ];
    },
    annotations(): readonly ParsedMark[] {
      const filesByRef = useMetadataStore().filesByRef;
      const config = useBrandStore().viewerConfig.evidenceThemes;

      const filtered = this.unfilteredAnnotations.filter(
        (a) =>
          /user/i.test(a.source) ||
          (a.evidenceThemes?.filter((et) => /accept/i.test(et.status)).length ?? 0) > 0 ||
          (config?.enableForUnknownClassifications ?? false) ||
          !/unknown/i.test(filesByRef[a.fileReference]?.aiClassification || "unknown"),
      );

      return Object.freeze(filtered);
    },

    annotationDictionary(): Record<string, readonly ParsedMark[]> {
      if (this.annotations.length < 1) {
        return {};
      }
      return Object.freeze(_.groupBy(this.annotations, "fileReference"));
    },
  },
  actions: {
    async load() {
      const brandStore = useBrandStore();
      if (brandStore.featuresConfig?.annotations === "disabled") return;

      const selectedCaseName = useCaseStore().selectedCaseName;
      if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

      if (this.moduleStatus === ModuleStatus.Loading) return;

      try {
        this.setAnnotationStatus(ModuleStatus.Loading);
        const annotations = await markService.loadMarks(
          brandStore.selectedClient,
          brandStore.selectedBrand,
          selectedCaseName,
          brandStore.selectedLens.code,
        );
        this.setAnnotations(annotations);
      } catch (e) {
        if (isApiError(e) && e.status === 204) {
          this.setAnnotations([]);
          return;
        }

        throw e;
      } finally {
        this.setAnnotationStatus(ModuleStatus.Ready);
      }
    },
    async save(payload: { annotation: ParsedMark }) {
      const oldAnnotation = this.annotations.find((a) => a.markId === payload.annotation.markId);

      // Annotation is already deleted and it's not new annotation. So don't save it
      if (!oldAnnotation && payload.annotation.supportingFileEntryFileReference) return;

      const brandStore = useBrandStore();
      if (brandStore.featuresConfig?.annotations === "disabled") return;

      const selectedCaseName = useCaseStore().selectedCaseName;
      if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

      try {
        await markService.saveMark(
          brandStore.selectedClient,
          brandStore.selectedBrand,
          selectedCaseName,
          payload.annotation.fileReference,
          { ...payload.annotation, parsedMarkData: undefined },
        );

        this.setAnnotations([
          ...this.unfilteredAnnotations.filter((a) => a.markId !== payload.annotation.markId),
          payload.annotation,
        ]);
      } finally {
        await Promise.all([this.load(), useEvidenceStore().loadEvidence()]);
      }
    },
    async reject(payload: { annotation: ParsedMark; reject: { all: true } | { themes: string[] } }) {
      const brandStore = useBrandStore();
      if (brandStore.featuresConfig?.annotations === "disabled") return;

      const selectedCaseName = useCaseStore().selectedCaseName;
      if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

      try {
        await markService.rejectMarkTheme(
          brandStore.selectedClient,
          brandStore.selectedBrand,
          selectedCaseName,
          payload.annotation.fileReference,
          payload.annotation.markId,
          payload.reject,
        );
      } finally {
        await Promise.all([this.load(), useEvidenceStore().loadEvidence()]);
      }
    },
    async delete(payload: { annotation: ParsedMark }) {
      const brandStore = useBrandStore();
      if (brandStore.featuresConfig?.annotations === "disabled") return;

      const selectedCaseName = useCaseStore().selectedCaseName;
      if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

      try {
        await markService.deleteMark(
          brandStore.selectedClient,
          brandStore.selectedBrand,
          selectedCaseName,
          payload.annotation.fileReference,
          payload.annotation.markId,
        );

        // Optimistically Update
        this.setAnnotations(this.unfilteredAnnotations.filter((mark) => mark.markId !== payload.annotation.markId));
      } finally {
        await Promise.all([this.load(), useEvidenceStore().loadEvidence()]);
      }
    },
    async redact(payload: { fileReference: string }) {
      const brandStore = useBrandStore();
      if (brandStore.featuresConfig?.annotations === "disabled") return;

      const selectedCaseName = useCaseStore().selectedCaseName;
      if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

      try {
        await markService.redact(
          brandStore.selectedClient,
          brandStore.selectedBrand,
          selectedCaseName,
          payload.fileReference,
        );
      } catch (e) {
        useMessageStore().updateMessage({
          message:
            (isApiError<{ reason?: string }>(e) && (e.status === 406 || e.status === 423)
              ? e.data?.reason
              : undefined) ?? "There has been a technical issue, and the redactions cannot be committed at this time",
          title: "Redaction Request",
          type: "error",
          showReload: false,
          autoHide: true,
        });

        throw e;
      } finally {
        this.redactFile();
      }
    },

    // Mutations
    redactFile() {
      // placeholder to trigger polling
    },
    setAnnotationStatus(status: ModuleStatus) {
      this.moduleStatus = status;
    },
    setAnnotations(annotations: Mark[]) {
      // TODO remove this logic when API fixes deleting themes
      this.unfilteredAnnotations = Object.freeze(
        annotations.map((a) => {
          let parsedMarkData;

          try {
            parsedMarkData = JSON.parse(a.markData) as RectangleAnnotation | HighlightAnnotation | TranscriptAnnotation;
            if ((!("type" in parsedMarkData) as never) && isRectangleAnnotation(parsedMarkData)) {
              parsedMarkData.type = "RectangleAnnotation";
            }
          } catch (e) {
            useMessageStore().updateMessage({
              title: "Error Loading Annotation",
              message: "Mark data does not exist or is incorrectly formatted",
              type: "error",
              showReload: false,
              autoHide: true,
            });
          }

          return {
            ...a,
            evidenceThemes: a.evidenceThemes?.filter((et) => et.status !== "__deleted__"),
            status: getMarkStatus(a),
            parsedMarkData,
          };
        }),
      );
    },
    reset(): void {
      this.moduleStatus = ModuleStatus.Initial;
      this.unfilteredAnnotations = [];
    },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    selectArea(_payload: HighlightCustomArea, _fileReference: string) {},
  },
});

interface State {
  unfilteredAnnotations: readonly ParsedMark[];
  moduleStatus: ModuleStatus;
}
